import { useEffect, useState } from "react";
import api, { inr } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Trophy, TrendingUp, Clock, Grid3x3, Target } from "lucide-react";

export default function Analytics() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [funnel, setFunnel] = useState([]);
  const [ttc, setTtc] = useState([]);
  const [heat, setHeat] = useState([]);
  const [board, setBoard] = useState([]);
  const [venue, setVenue] = useState("all");
  const [venues, setVenues] = useState([]);

  useEffect(() => { if (isAdmin) api.get("/venues").then((r) => setVenues(r.data)); }, [isAdmin]);

  useEffect(() => {
    const params = venue !== "all" ? { venue } : {};
    api.get("/analytics/funnel", { params }).then((r) => setFunnel(r.data));
    api.get("/analytics/time-to-convert").then((r) => setTtc(r.data));
    api.get("/analytics/heatmap", { params }).then((r) => setHeat(r.data));
    api.get("/analytics/leaderboard", { params }).then((r) => setBoard(r.data));
  }, [venue]);

  const heatColor = (v, max) => {
    if (!v) return "bg-slate-50 text-slate-300";
    const i = v / (max || 1);
    if (i > 0.66) return "bg-[#0F4C81] text-white";
    if (i > 0.33) return "bg-[#0F4C81]/50 text-white";
    return "bg-[#0F4C81]/20 text-[#0F4C81]";
  };
  const heatMax = Math.max(1, ...heat.flatMap((r) => [r.Hot, r.Warm, r.Cold]));

  return (
    <div>
      <PageHeader title="Analytics" subtitle="Conversion, velocity and team performance."
        actions={isAdmin && venues.length > 0 ? (
          <Select value={venue} onValueChange={setVenue}>
            <SelectTrigger className="w-48" data-testid="analytics-venue-filter"><SelectValue placeholder="All branches" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Branches</SelectItem>
              {venues.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        ) : null}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card className="p-6" data-testid="conversion-funnel">
          <div className="text-sm font-bold text-slate-700 mb-5 flex items-center gap-2"><TrendingUp size={16} />Conversion Funnel</div>
          <div className="space-y-2.5">
            {funnel.map((f, i) => (
              <div key={f.stage} className="flex items-center gap-3">
                <div className="w-36 text-sm text-slate-600 shrink-0">{f.stage}</div>
                <div className="flex-1 h-8 bg-slate-100 rounded-lg overflow-hidden relative">
                  <div className="h-full rounded-lg flex items-center justify-end pr-3 text-white text-xs font-bold" style={{ width: `${Math.max(f.pct, 6)}%`, background: `linear-gradient(90deg,#0F4C81,#10B981)`, opacity: 1 - i * 0.09 }}>
                    {f.count}
                  </div>
                </div>
                <div className="w-12 text-right text-sm font-mono font-semibold text-slate-500">{f.pct}%</div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6" data-testid="time-to-convert">
          <div className="text-sm font-bold text-slate-700 mb-5 flex items-center gap-2"><Clock size={16} />Time-to-Convert (avg days)</div>
          <div className="space-y-3">
            {ttc.map((t) => {
              const max = Math.max(...ttc.map((x) => x.avg_days), 1);
              return (
                <div key={t.stage} className="flex items-center gap-3">
                  <div className="w-40 text-sm text-slate-600 shrink-0">→ {t.stage}</div>
                  <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-amber-400 rounded-full" style={{ width: `${(t.avg_days / max) * 100}%` }} /></div>
                  <div className="w-14 text-right text-sm font-mono font-semibold text-slate-700">{t.avg_days}d</div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      <Card className="p-6 mb-6" data-testid="rm-leaderboard">
        <div className="text-sm font-bold text-slate-700 mb-5 flex items-center gap-2"><Trophy size={16} />RM Leaderboard</div>
        <div className="space-y-2">
          {board.map((r, i) => (
            <div key={r.username} className="flex items-center gap-4 p-3 rounded-lg border border-slate-100 hover:bg-slate-50" data-testid={`leader-${r.username}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${i === 0 ? "bg-amber-100 text-amber-700" : i === 1 ? "bg-slate-200 text-slate-600" : i === 2 ? "bg-orange-100 text-orange-700" : "bg-slate-100 text-slate-400"}`}>{i + 1}</div>
              <div className="flex-1"><div className="font-semibold text-slate-900">{r.rm}</div><div className="text-xs text-slate-400">{r.total_customers} customers · {r.mela_checkins} mela check-ins · {r.invite_leads} via link</div></div>
              <div className="text-right"><div className="font-mono font-bold text-slate-900">{inr(r.disbursed_amount)}</div><div className="text-xs text-slate-400">{r.disbursed_count} disbursed</div></div>
              <div className="w-16 text-right"><div className="font-mono font-bold text-emerald-600">{r.conversion}%</div><div className="text-xs text-slate-400">conv.</div></div>
            </div>
          ))}
          {board.length === 0 && <div className="text-sm text-slate-400 text-center py-4">No RMs yet.</div>}
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6" data-testid="priority-heatmap">
          <div className="text-sm font-bold text-slate-700 mb-5 flex items-center gap-2"><Grid3x3 size={16} />Priority Heatmap per RM</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-slate-400 text-xs"><th className="text-left font-medium pb-2">RM</th><th className="font-medium pb-2">Hot</th><th className="font-medium pb-2">Warm</th><th className="font-medium pb-2">Cold</th></tr></thead>
              <tbody>
                {heat.map((r) => (
                  <tr key={r.username}>
                    <td className="py-1.5 text-slate-700 font-medium">{r.rm}</td>
                    {["Hot", "Warm", "Cold"].map((p) => (
                      <td key={p} className="p-1"><div className={`h-9 rounded-md flex items-center justify-center font-bold font-mono ${heatColor(r[p], heatMax)}`}>{r[p]}</div></td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card className="p-6" data-testid="team-goal-board">
          <div className="text-sm font-bold text-slate-700 mb-5 flex items-center gap-2"><Target size={16} />Team Goal Board</div>
          <div className="space-y-4">
            {board.map((r) => {
              const pct = r.target > 0 ? Math.min(100, Math.round((r.disbursed_amount / r.target) * 100)) : 0;
              return (
                <div key={r.username} data-testid={`goal-${r.username}`}>
                  <div className="flex justify-between text-sm mb-1"><span className="font-medium text-slate-700">{r.rm}</span><span className="font-mono text-slate-500">{inr(r.disbursed_amount)} / {inr(r.target)}</span></div>
                  <div className="h-3 bg-slate-100 rounded-full overflow-hidden"><div className={`h-full rounded-full ${pct >= 100 ? "bg-emerald-500" : "bg-[#0F4C81]"}`} style={{ width: `${pct}%` }} /></div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
}
